import React, { useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Copy, RefreshCcw, Eye, EyeOff, Terminal, CheckCircle2, User, Globe, Lock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [show, setShow] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [profile, setProfile] = useState({
    username: user?.username || "",
    bio: user?.bio || "",
    public: user?.public ?? true,
    default_movie_public: user?.default_movie_public ?? true,
  });
  const [savingProfile, setSavingProfile] = useState(false);

  React.useEffect(() => {
    if (user) setProfile({
      username: user.username || "",
      bio: user.bio || "",
      public: user.public ?? true,
      default_movie_public: user.default_movie_public ?? true,
    });
  }, [user]);

  if (!user) return null;

  const apiKey = user.api_key;
  const base = `${BACKEND_URL}/api/mcp`;
  const mcpUrl = `${BACKEND_URL}/api/mcp`;
  const profileUrl = profile.username ? `${window.location.origin}/u/${profile.username}` : "";

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const rotate = async () => {
    if (!window.confirm("Rotate your API key? Existing Claude Code integrations will break until updated.")) return;
    setRotating(true);
    try {
      await api.post("/auth/rotate-api-key");
      await checkAuth();
      toast.success("New API key generated");
    } catch { toast.error("Failed"); }
    finally { setRotating(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", profile);
      await checkAuth();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save profile");
    } finally { setSavingProfile(false); }
  };

  const claudeConfig = JSON.stringify({
    mcpServers: {
      reelkeeper: {
        type: "http",
        url: mcpUrl,
        headers: { "X-API-Key": show ? apiKey : "YOUR_API_KEY" },
      },
    },
  }, null, 2);

  const mcpExamples = [
    { label: "List tools",
      code: `curl -X POST "${mcpUrl}" \\
  -H "X-API-Key: ${show ? apiKey : "YOUR_API_KEY"}" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'` },
    { label: "Add a movie",
      code: `curl -X POST "${mcpUrl}" \\
  -H "X-API-Key: ${show ? apiKey : "YOUR_API_KEY"}" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/call","params":{"name":"add_movie","arguments":{"title":"Iron Man","imdb_id":"tt0371746","tags":["mcu"]}}}'` },
    { label: "Toggle a TV season",
      code: `curl -X POST "${mcpUrl}" \\
  -H "X-API-Key: ${show ? apiKey : "YOUR_API_KEY"}" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/call","params":{"name":"toggle_season_watched","arguments":{"id":"MOVIE_ID","season_number":1}}}'` },
  ];

  const restExamples = [
    { label: "Verify key", code: `curl -H "X-API-Key: ${show ? apiKey : "YOUR_API_KEY"}" ${base}/ping` },
    { label: "List backlog", code: `curl -H "X-API-Key: ${show ? apiKey : "YOUR_API_KEY"}" "${base}/movies?status=backlog"` },
    { label: "Suggest next watch", code: `curl -H "X-API-Key: ${show ? apiKey : "YOUR_API_KEY"}" ${base}/suggest` },
  ];

  return (
    <div className="space-y-10">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-[#E11D48] font-bold mb-2">Settings</p>
        <h1 className="font-display text-4xl font-bold tracking-tighter">Profile, MCP & API</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">Manage your public profile and Claude Code MCP key.</p>
      </div>

      {/* Profile */}
      <div className="p-6 bg-[#0F0F12] rounded-xl border border-white/5 space-y-5" data-testid="profile-panel">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#E11D48] font-bold">
          <User className="w-3.5 h-3.5" /> Public profile
        </div>
        <div className="flex items-center gap-4">
          {user.picture && <img src={user.picture} alt="" className="w-14 h-14 rounded-full border border-white/10" />}
          <div>
            <div className="font-display text-lg font-semibold">{user.name}</div>
            <div className="text-sm text-slate-400">{user.email}</div>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Username (used in your public URL)</div>
          <div className="flex gap-2">
            <span className="bg-[#1A1A1E] border border-white/10 rounded-l-md px-3 py-2 text-sm text-slate-500">/u/</span>
            <Input
              value={profile.username}
              onChange={e => setProfile({ ...profile, username: e.target.value.toLowerCase() })}
              placeholder="your-handle"
              data-testid="profile-username-input"
              className="bg-[#1A1A1E] border-white/10 rounded-l-none focus-visible:ring-[#E11D48] text-white" />
          </div>
          {profile.username && profile.public && (
            <div className="mt-2 text-xs text-slate-500 flex items-center gap-2">
              Public link: <Link to={`/u/${profile.username}`} className="text-[#E11D48] hover:underline" data-testid="profile-public-link">{profileUrl}</Link>
              <button onClick={() => copy(profileUrl, "Profile URL")} className="text-slate-400 hover:text-white"><Copy className="w-3 h-3" /></button>
            </div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Bio</div>
          <Textarea
            value={profile.bio || ""}
            onChange={e => setProfile({ ...profile, bio: e.target.value })}
            placeholder="What you watch. Why you watch."
            rows={2}
            data-testid="profile-bio-input"
            className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white" />
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-[#1A1A1E] border border-white/5">
          <div>
            <div className="text-sm font-medium flex items-center gap-2">
              {profile.public ? <Globe className="w-4 h-4 text-[#10B981]" /> : <Lock className="w-4 h-4 text-slate-500" />}
              Profile is {profile.public ? "public" : "private"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {profile.public ? "Anyone with the link can browse your public groups & movies." : "Only you can see your library."}
            </div>
          </div>
          <Switch
            checked={profile.public}
            onCheckedChange={(v) => setProfile({ ...profile, public: v })}
            data-testid="profile-public-toggle" />
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-[#1A1A1E] border border-white/5">
          <div>
            <div className="text-sm font-medium">Show ungrouped movies publicly</div>
            <div className="text-xs text-slate-500 mt-1">When off, only movies inside public groups are visible.</div>
          </div>
          <Switch
            checked={profile.default_movie_public}
            onCheckedChange={(v) => setProfile({ ...profile, default_movie_public: v })}
            data-testid="profile-default-public-toggle" />
        </div>

        <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn" className="bg-[#E11D48] hover:bg-rose-700">
          Save profile
        </Button>
      </div>

      {/* API key */}
      <div className="p-6 bg-[#0F0F12] rounded-xl border border-white/5 space-y-4" data-testid="apikey-panel">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#E11D48] font-bold">
          <Terminal className="w-3.5 h-3.5" /> Your API key
        </div>
        <div className="flex gap-2 items-center">
          <code className="flex-1 font-mono text-sm bg-[#1A1A1E] border border-white/5 rounded-lg px-4 py-3 break-all" data-testid="apikey-value">
            {show ? apiKey : "•".repeat(Math.min(apiKey.length, 40))}
          </code>
          <Button variant="outline" size="icon" onClick={() => setShow(s => !s)} data-testid="apikey-toggle" className="bg-white/5 border-white/10 hover:bg-white/10">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="icon" onClick={() => copy(apiKey, "API key")} data-testid="apikey-copy" className="bg-white/5 border-white/10 hover:bg-white/10">
            <Copy className="w-4 h-4" />
          </Button>
          <Button onClick={rotate} disabled={rotating} data-testid="apikey-rotate" className="bg-[#E11D48] hover:bg-rose-700">
            <RefreshCcw className={`w-4 h-4 mr-1.5 ${rotating ? "animate-spin" : ""}`} /> Rotate
          </Button>
        </div>
        <div className="text-xs text-slate-500">Send as <code className="font-mono text-slate-300">X-API-Key</code> header. Keep it secret.</div>
      </div>

      {/* MCP URL */}
      <div className="p-6 bg-[#0F0F12] rounded-xl border border-white/5 space-y-3" data-testid="mcpurl-panel">
        <div className="text-xs uppercase tracking-[0.2em] text-[#E11D48] font-bold">MCP endpoint (JSON-RPC 2.0, Streamable HTTP)</div>
        <div className="flex gap-2">
          <code className="flex-1 font-mono text-sm bg-[#1A1A1E] border border-white/5 rounded-lg px-4 py-3 break-all">{mcpUrl}</code>
          <Button variant="outline" size="icon" onClick={() => copy(mcpUrl, "MCP URL")} data-testid="mcpurl-copy" className="bg-white/5 border-white/10 hover:bg-white/10">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
        <div className="text-xs text-slate-500">Methods: <code className="text-slate-300">initialize · ping · tools/list · tools/call</code> · 31 tools · Transport: <code className="text-slate-300">streamable-http</code></div>
      </div>

      {/* Claude Code config */}
      <div className="p-6 bg-gradient-to-br from-[#E11D48]/10 to-transparent border border-[#E11D48]/30 rounded-xl space-y-3" data-testid="claude-config">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-[#E11D48] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display text-lg font-semibold mb-1">Claude Code / Claude Desktop config</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Add this to your MCP client config. Claude will discover all 31 tools — including season tracking, public-profile browsing, and copy-watchlist.
            </p>
          </div>
        </div>
        <div className="bg-[#0F0F12] border border-white/5 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
            <div className="text-sm text-slate-300">mcp client config</div>
            <button onClick={() => copy(claudeConfig, "MCP config")} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="copy-claude-config">
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <pre className="p-4 text-xs font-mono text-slate-300 overflow-x-auto">{claudeConfig}</pre>
        </div>
      </div>

      {/* MCP JSON-RPC Examples */}
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-[0.2em] text-[#E11D48] font-bold">MCP (JSON-RPC) examples</div>
        {mcpExamples.map((ex, i) => (
          <div key={i} className="bg-[#0F0F12] border border-white/5 rounded-xl overflow-hidden" data-testid={`mcp-example-${i}`}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
              <div className="text-sm text-slate-300">{ex.label}</div>
              <button onClick={() => copy(ex.code, ex.label)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid={`mcp-copy-${i}`}>
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <pre className="p-4 text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre-wrap">{ex.code}</pre>
          </div>
        ))}
      </div>

      {/* REST convenience */}
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Plain REST (convenience, no JSON-RPC)</div>
        {restExamples.map((ex, i) => (
          <div key={i} className="bg-[#0F0F12] border border-white/5 rounded-xl overflow-hidden" data-testid={`rest-example-${i}`}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
              <div className="text-sm text-slate-300">{ex.label}</div>
              <button onClick={() => copy(ex.code, ex.label)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <pre className="p-4 text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre-wrap">{ex.code}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}
